// Global Instructions Rule Applied!
//
// Commitment form — one investor's commitment to one fund (a
// public.fund_commitments row) as a record in its own app tab (funds /
// investors amendments PR 5, 2026-10-02). It replaces the New / Edit
// Commitment pop-up of the fund Investors tab.
//
//   /commitments/forms/full/<fund_commitments.id>   this form
//   /commitments/forms/full/new?...                 the New commitment form
//
// A STANDALONE commitment is edited whole: Investor (the account and the
// investor entity - another of the account's, another account's, or a new
// entity created with the save; owner 2026-10-12), Commitment (amount,
// status, the two dates, the GP marker, the carry marker - the row the fund's
// carried interest is paid to, which may be a commitment of 0), Terms (its
// own fee, carry and hurdle figures - all blank to use the fund's Carried
// Interest terms, or the full side-letter set) and Capital call notices (the
// investor's additional notice documents, or the commitment's own). [Delete]
// [Cancel | Close] [Save]; Save and Cancel only while there are unsaved
// changes.
//
// A POCKET of a consolidated commitment (the commitment of one member fund)
// shows the same sections read-only under a pointer to the consolidated
// commitment, where its investor, amount, status, dates and notice documents
// are set. Its two markers are its own: the GP marker, and the carry marker,
// since the row a fund's carried interest goes to is chosen per member fund.
// No Delete.

import React from 'react'
import { Alert, Button, Col, Row, Spin } from 'antd'

import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { useTabManagerContext } from '../../../../core/app-layout/lib/TabManagerContext.tsx'
import { useRefreshOnActivate } from '../../../../core/hooks/useRefreshOnActivate.ts'
import {
  TABLE_ACCOUNTS,
  TABLE_CONSOLIDATED_COMMITMENTS,
  TABLE_FUNDS,
  TABLE_INVESTORS
} from '../../../../state_manager/globals.ts'
import { commitmentTabTitle, useCommitmentForm } from '../../hooks/useCommitmentForm.tsx'
import { CommitmentFormHeader } from '../CommitmentFormHeader.tsx'
import { CommitmentNoticeSection, NOTICE_POCKET_REASON } from '../CommitmentNoticeSection.tsx'
import { CommitmentTermsSection } from '../CommitmentTermsSection.tsx'
import NewCommitmentForm from './NewCommitmentForm.tsx'
import { fundCarryDefaultsOf } from '../../../../core/utils/fundCarryDefaults.ts'

interface FullCommitmentFormProps {
  recordId?: string | null
  isNew?: boolean
}

function EditCommitmentForm({ recordId }: { recordId: string }): React.ReactElement {
  const form = useCommitmentForm(recordId)
  const { openFormInTab } = useTabManagerContext()
  // a change made elsewhere (the consolidated commitment, the investor's
  // notice documents) shows when the user comes back to this tab
  const attachRefresh = useRefreshOnActivate(form.refreshIfClean, { maxAgeMs: 0 })
  const { detail } = form

  if (form.loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (!detail) {
    return (
      <div className='p-10 text-center text-gray-500'>
        {form.notFound ? 'Commitment not found.' : form.loadError || 'The commitment could not be loaded.'}
        <div className='mt-3'>
          <Button onClick={form.handleClose}>Close</Button>
        </div>
      </div>
    )
  }

  const inputProps = {
    inputMode: 'edit' as const,
    labelPosition: 'above' as const,
    onChange: form.handleChange,
    validationErrors: form.validationErrors,
    validationErrorsVersion: form.validationErrorsVersion
  }
  const consolidatedCommitmentId = detail.fund_commitments__fund_group_commitment_id

  return (
    <div ref={attachRefresh} className='h-full overflow-y-auto p-4'>
      {form.modalContextHolder}
      <CommitmentFormHeader
        title={commitmentTabTitle(detail.investors__name, detail.funds__name)}
        links={[
          {
            label: 'Investor',
            text: detail.investors__name,
            onClick: () => openFormInTab(TABLE_INVESTORS, detail.investors__id, detail.investors__name)
          },
          {
            label: 'Account',
            text: detail.accounts__name,
            onClick: () => openFormInTab(TABLE_ACCOUNTS, detail.accounts__id, detail.accounts__name)
          },
          {
            label: 'Fund',
            text: detail.funds__name,
            onClick: () => openFormInTab(TABLE_FUNDS, detail.funds__id, detail.funds__name)
          }
        ]}
        dirty={form.dirty}
        saving={form.saving}
        onSave={() => void form.handleSave()}
        onCancel={form.handleCancel}
        onClose={form.handleClose}
        onDelete={form.isPocket ? undefined : form.handleDelete}
        deleting={form.deleting}
      />

      {form.isPocket && consolidatedCommitmentId ? (
        <Alert
          className='mb-3'
          type='info'
          showIcon
          message={
            <span>
              Part of {detail.investors__name ?? 'the investor'}&apos;s consolidated commitment to{' '}
              {detail.fund_groups__name ?? 'the consolidated fund'}. Its amount, status, dates and notice documents are
              set there.{' '}
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() =>
                  openFormInTab(
                    TABLE_CONSOLIDATED_COMMITMENTS,
                    consolidatedCommitmentId,
                    commitmentTabTitle(detail.investors__name, detail.fund_groups__name)
                  )
                }
              >
                Open the consolidated commitment
              </button>
            </span>
          }
        />
      ) : null}

      <Row gutter={[12, 12]}>
        {/* the investor entity (owner 2026-10-12): a standalone commitment can
            move to another investor of the account, another account, or a new
            entity created with the save; a pocket's is the subscription's */}
        <Col xs={24} xl={12}>
          <FormSection title='Investor' titleColor='blue' scrollable={false}>
            {form.isPocket ? (
              <InputManager inputMode='read' labelPosition='above' fields={form.entityFields} />
            ) : (
              <>
                <InputManager key={`entityAccount-${form.resetVersion}`} {...inputProps} fields={form.entityFields.slice(0, 1)} />
                <InputManager
                  key={`entityInvestor-${form.resetVersion}-${form.accountKey}`}
                  {...inputProps}
                  fields={form.entityFields.slice(1)}
                />
                {form.newInvestorFields.length > 0 ? (
                  <InputManager key={`newInvestor-${form.resetVersion}`} {...inputProps} fields={form.newInvestorFields} />
                ) : null}
              </>
            )}
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Commitment' titleColor='blue' scrollable={false}>
            {form.isPocket ? (
              <InputManager inputMode='read' labelPosition='above' fields={form.commitmentFields} />
            ) : (
              <InputManager key={`commitmentFields-${form.resetVersion}`} {...inputProps} fields={form.commitmentFields} />
            )}
            <InputManager key={`markerFields-${form.resetVersion}`} {...inputProps} fields={form.markerFields} />
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <CommitmentTermsSection
            terms={form.draft.terms}
            fundFeeRate={detail.funds__mgmt_fee_rate}
            fundDefaults={fundCarryDefaultsOf(detail)}
            onChange={form.handleChange}
            readOnly={form.isPocket}
            resetVersion={form.resetVersion}
          />
        </Col>
        <Col xs={24}>
          <CommitmentNoticeSection
            notice={form.draft.notice}
            investorTemplateIds={detail.prog__investor_notice_supplement_template_ids}
            options={form.noticeSupplementOptions}
            onChange={form.handleNoticeChange}
            readOnlyReason={form.isPocket ? NOTICE_POCKET_REASON : null}
            resetVersion={form.resetVersion}
          />
        </Col>
      </Row>
    </div>
  )
}

export default function FullCommitmentForm({
  recordId = null,
  isNew = false
}: FullCommitmentFormProps): React.ReactElement {
  if (isNew || !recordId) return <NewCommitmentForm />
  return <EditCommitmentForm recordId={recordId} />
}
